import React, { useState, useEffect, useContext, useLayoutEffect } from 'react'
import { format } from 'date-fns'
import { tr, es, ca, enUS } from 'date-fns/locale'
import { useIntl } from 'react-intl'
import { DateRangeInput } from '../../../../utils/Form/Widgets/DateRangeInput'
import Select from '../../../../utils/Form/Widgets/Select'
import AutocompleteSelect from '../../../../utils/Form/Widgets/AutocompleteSelect'
import { CustomizationContext } from '../../../customization/CustomizationContext'
import { getAlertType } from '../../api/requests'

interface SiteAlarm {
  site_id: number
  site_name: string
  alarm_id: number
  alarm_count: number
  site_acknowledge?: boolean
  site_alarm_comment?: string
}

interface CompanyAlarms {
  company_name: string
  company_alarm_count: number
  sites: SiteAlarm[]
  acknowledged_count?: number
  unacknowledged_count?: number
}

interface DateAlarms {
  [companyId: string]: CompanyAlarms
}

interface GroupedAlarmsResponse {
  data: {
    [date: string]: DateAlarms
  }
  pagination: {
    total: number
    per_page: number
    current_page: number
    last_page: number
  }
}

interface GroupedAlarmsTableProps {
  data: GroupedAlarmsResponse
  title?: string
  fetchData: (
    page: number,
    filters?: {
      startDate?: Date
      endDate?: Date
      siteAcknowledge?: boolean
      companyId?: number
      alertType?: 'hourly' | 'daily'
    }
  ) => void
  editCallback: (alarmId: number, fetchData: (page: number) => void) => void
}

const GroupedAlarmsTable: React.FC<GroupedAlarmsTableProps> = (props) => {
  // @ts-ignore
  const { setSettingsAndUpdate, settings } = useContext(CustomizationContext)
  const [expandedDates, setExpandedDates] = useState<{ [key: string]: boolean }>({})
  const [expandedCompanies, setExpandedCompanies] = useState<{ [key: string]: boolean }>({})
  const [isLoading, setIsLoading] = useState(false)
  const intl = useIntl()
  const [siteFilter, setSiteFilter] = useState('')
  const [dateRange, setDateRange] = useState<string>('')
  const [siteAcknowledgeFilter, setSiteAcknowledgeFilter] = useState<boolean | undefined>(undefined)
  const [companyFilter, setCompanyFilter] = useState<number | null>(
    settings?.companyS ? parseInt(settings.companyS) : null
  )
  const [alertTypeFilter, setAlertTypeFilter] = useState<'hourly' | 'daily'>(
    (settings?.alertType as 'hourly' | 'daily') ?? 'hourly'
  )
  const [companyAlertType, setCompanyAlertType] = useState<'hourly' | 'daily' | 'both'>('both')

  const loadCompanyAlertType = async (
    companyId: number,
    doFetchData: boolean,
    resetFilters: boolean = false
  ) => {
    const alertType = await getAlertType(companyId)
    let normalizedCompanyType: 'hourly' | 'daily' | 'both'
    if (alertType === 'hourly' || alertType === 'daily' || alertType === 'both') {
      normalizedCompanyType = alertType
    } else {
      normalizedCompanyType = 'both'
    }
    setCompanyAlertType(normalizedCompanyType)

    const nextAlertType: 'hourly' | 'daily' =
      normalizedCompanyType === 'both'
        ? (settings?.alertType as 'hourly' | 'daily') ?? 'hourly'
        : normalizedCompanyType === 'daily'
        ? 'daily'
        : 'hourly'

    const prevAlertType = alertTypeFilter
    setAlertTypeFilter(nextAlertType)

    setSettingsAndUpdate({
      ...settings,
      alertType: nextAlertType,
    })

    const shouldFetch =
      doFetchData &&
      (resetFilters || nextAlertType !== prevAlertType || companyId !== companyFilter)

    if (shouldFetch && props.fetchData) {
      setIsLoading(true)
      const filters: {
        startDate?: Date
        endDate?: Date
        siteAcknowledge?: boolean
        companyId?: number
        alertType?: 'hourly' | 'daily'
      } = {}

      if (!resetFilters) {
        if (dateRange) {
          const [startDateStr, endDateStr] = dateRange.split(' to ')
          if (startDateStr && endDateStr) {
            filters.startDate = new Date(startDateStr)
            filters.endDate = new Date(endDateStr)
          }
        }

        if (siteAcknowledgeFilter !== undefined) {
          filters.siteAcknowledge = siteAcknowledgeFilter
        }
      }

      filters.companyId = companyId
      filters.alertType = nextAlertType

      props.fetchData(1, filters)
    }
  }

  useLayoutEffect(() => {
    if (companyFilter) {
      loadCompanyAlertType(companyFilter, false)
    } else {
      setAlertTypeFilter('hourly')
      setSettingsAndUpdate({
        ...settings,
        alertType: 'hourly',
      })
    }
  }, [])
  useEffect(() => {
    if (props.data?.data) {
      const dates = Object.keys(props.data.data)
      if (dates.length > 0) {
        const sortedDates = dates.sort((a, b) => {
          return new Date(b).getTime() - new Date(a).getTime()
        })

        setExpandedDates((prev) => ({
          ...prev,
          [sortedDates[0]]: true,
        }))
      }
    }
  }, [props.data])

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString)
      const currentLocale = intl.locale
      let locale = enUS
      if (currentLocale === 'tr') {
        locale = tr
      } else if (currentLocale === 'es') {
        locale = es
      } else if (currentLocale === 'ca') {
        locale = ca
      }
      return format(date, 'd MMMM yyyy', { locale })
    } catch (e) {
      console.error('Error formatting date:', e)
      return dateString
    }
  }

  const toggleDateExpansion = (date: string) => {
    setExpandedDates((prev) => ({
      ...prev,
      [date]: !prev[date],
    }))
  }

  const toggleCompanyExpansion = (dateKey: string, companyId: string) => {
    const key = `${dateKey}-${companyId}`
    setExpandedCompanies((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const handlePageChange = (page: number) => {
    if (
      page < 1 ||
      page > props.data.pagination.last_page ||
      page === props.data.pagination.current_page
    )
      return

    setIsLoading(true)

    // Call the fetchData function with the new page and filters
    if (props.fetchData) {
      const filters: {
        startDate?: Date
        endDate?: Date
        siteAcknowledge?: boolean
        companyId?: number
        alertType?: 'hourly' | 'daily'
      } = {}

      if (dateRange) {
        const [startDateStr, endDateStr] = dateRange.split(' to ')
        if (startDateStr && endDateStr) {
          filters.startDate = new Date(startDateStr)
          filters.endDate = new Date(endDateStr)
        }
      }

      if (siteAcknowledgeFilter !== undefined) {
        filters.siteAcknowledge = siteAcknowledgeFilter
      }

      if (companyFilter !== null) {
        filters.companyId = companyFilter
      }

      if (alertTypeFilter !== undefined) {
        filters.alertType = alertTypeFilter
      }

      props.fetchData(page, filters)
    }
  }

  // Reset loading state when data changes
  useEffect(() => {
    if (props.data) {
      setIsLoading(false)
    }
  }, [props.data])

  const applyDateFilters = () => {
    setIsLoading(true)
    if (props.fetchData) {
      const filters: {
        startDate?: Date
        endDate?: Date
        siteAcknowledge?: boolean
        companyId?: number
        alertType?: 'hourly' | 'daily'
      } = {}

      if (dateRange) {
        const [startDateStr, endDateStr] = dateRange.split(' to ')
        if (startDateStr && endDateStr) {
          filters.startDate = new Date(startDateStr)
          filters.endDate = new Date(endDateStr)
        }
      }

      if (siteAcknowledgeFilter !== undefined) {
        filters.siteAcknowledge = siteAcknowledgeFilter
      }

      if (companyFilter !== null) {
        filters.companyId = companyFilter
      }

      if (alertTypeFilter !== undefined) {
        filters.alertType = alertTypeFilter
      }

      props.fetchData(1, filters)
    }
  }

  const resetDateFilters = async () => {
    setDateRange('')
    setSiteAcknowledgeFilter(undefined)
    const defaultCompanyId = settings?.companyS ? parseInt(settings.companyS) : null
    setCompanyFilter(defaultCompanyId)

    if (defaultCompanyId !== null) {
      await loadCompanyAlertType(defaultCompanyId, true, true)
    } else {
      const nextAlertType = (settings?.alertType as 'hourly' | 'daily') ?? 'daily'
      setAlertTypeFilter(nextAlertType)
      setIsLoading(true)
      if (props.fetchData) {
        const filters: {
          startDate?: Date
          endDate?: Date
          siteAcknowledge?: boolean
          companyId?: number
          alertType?: 'hourly' | 'daily'
        } = {}
        filters.alertType = nextAlertType
        props.fetchData(1, filters)
      }
    }
  }

  const handleDateRangeChange = (e: any) => {
    setDateRange(e.target.value)
  }

  const handleAcknowledgeFilterChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const value = event.target.value
    setSiteAcknowledgeFilter(value === '' ? undefined : value === 'true')

    const filters: {
      startDate?: Date
      endDate?: Date
      siteAcknowledge?: boolean
      companyId?: number
      alertType?: 'hourly' | 'daily'
    } = {}

    if (dateRange) {
      const [startDateStr, endDateStr] = dateRange.split(' to ')
      if (startDateStr && endDateStr) {
        filters.startDate = new Date(startDateStr)
        filters.endDate = new Date(endDateStr)
      }
    }

    if (value !== '') {
      filters.siteAcknowledge = value === 'true'
    }

    if (companyFilter !== null) {
      filters.companyId = companyFilter
    }

    if (alertTypeFilter !== undefined) {
      filters.alertType = alertTypeFilter
    }

    props.fetchData(1, filters)
  }

  const handleCompanyFilterChange = async (event: any): Promise<void> => {
    const value = event.target.value

    if (value === null || value === '') {
      setCompanyFilter(null)
      setSettingsAndUpdate({
        ...settings,
        companyS: undefined,
      })

      // No company selected: default to hourly and fetch once
      setAlertTypeFilter('hourly')
      setSettingsAndUpdate({
        ...settings,
        alertType: 'hourly',
      })

      const filters: {
        startDate?: Date
        endDate?: Date
        siteAcknowledge?: boolean
        companyId?: number
        alertType?: 'hourly' | 'daily'
      } = {}

      if (dateRange) {
        const [startDateStr, endDateStr] = dateRange.split(' to ')
        if (startDateStr && endDateStr) {
          filters.startDate = new Date(startDateStr)
          filters.endDate = new Date(endDateStr)
        }
      }

      if (siteAcknowledgeFilter !== undefined) {
        filters.siteAcknowledge = siteAcknowledgeFilter
      }

      filters.alertType = 'hourly'

      setIsLoading(true)
      props.fetchData(1, filters)
    } else {
      const newCompanyId = parseInt(value)
      setCompanyFilter(newCompanyId)
      setSettingsAndUpdate({
        ...settings,
        companyS: value,
      })

      await loadCompanyAlertType(newCompanyId, true)
    }
  }

  const getCardClassName = (siteAlarm: SiteAlarm) => {
    return siteAlarm.site_acknowledge ? 'card-success' : 'card-danger'
  }

  const renderDateFilters = () => {
    const acknowledgeOptions = [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ]
    const handleAlertTypeFilterChange = (type: 'hourly' | 'daily') => {
      setAlertTypeFilter(type)
      if (companyAlertType === 'both') {
        setSettingsAndUpdate({
          ...settings,
          alertType: type,
        })
      }
      setIsLoading(true)

      const filters: {
        startDate?: Date
        endDate?: Date
        siteAcknowledge?: boolean
        companyId?: number
        alertType?: 'hourly' | 'daily'
      } = {}

      if (dateRange) {
        const [startDateStr, endDateStr] = dateRange.split(' to ')
        if (startDateStr && endDateStr) {
          filters.startDate = new Date(startDateStr)
          filters.endDate = new Date(endDateStr)
        }
      }

      if (siteAcknowledgeFilter !== undefined) {
        filters.siteAcknowledge = siteAcknowledgeFilter
      }

      if (companyFilter !== null) {
        filters.companyId = companyFilter
      }

      filters.alertType = type

      props.fetchData(1, filters)
    }

    return (
      <div className='date-filters mb-4'>
        <div className='card'>
          <div className='card-body'>
            <div className='row mb-4'>
              <div className='col-12'>
                <div className='d-flex justify-content-center'>
                  <div className='btn-group alarm-type-filter' role='group'>
                    {companyAlertType === 'both' && (
                      <>
                        <button
                          type='button'
                          className={`btn ${
                            alertTypeFilter === 'hourly' ? 'btn-primary' : 'btn-outline-primary'
                          }`}
                          onClick={() => handleAlertTypeFilterChange('hourly')}
                        >
                          <i className='fas fa-clock me-2'></i>
                          {intl.formatMessage({ id: 'ALARMS.HALFHOURLY' }) || 'Half Hourly Alarms'}
                        </button>
                        <button
                          type='button'
                          className={`btn ${
                            alertTypeFilter === 'daily' ? 'btn-primary' : 'btn-outline-primary'
                          }`}
                          onClick={() => handleAlertTypeFilterChange('daily')}
                        >
                          <i className='fas fa-calendar-day me-2'></i>
                          {intl.formatMessage({ id: 'ALARMS.DAILY' }) || 'Daily Alarms'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className='row g-3'>
              <div className='col-md-3'>
                <AutocompleteSelect
                  title='Company'
                  name='company'
                  onChange={handleCompanyFilterChange}
                  defaultValue={companyFilter !== null ? companyFilter.toString() : ''}
                  autoCompleteTokenUrl='/company/autocomplete'
                />
              </div>
              <div className='col-md-3'>
                <DateRangeInput
                  name='dateRange'
                  title={intl.formatMessage({ id: 'DATERANGE' })}
                  seperator='to'
                  onChange={handleDateRangeChange}
                  placeholder='Select Date Range'
                  ranges={[
                    {
                      startDate: dateRange ? new Date(dateRange.split(' to ')[0]) : undefined,
                      endDate: dateRange ? new Date(dateRange.split(' to ')[1]) : undefined,
                      key: 'selection',
                    },
                  ]}
                />
              </div>
              <div className='col-md-3'>
                <Select
                  name='acknowledgeFilter'
                  title='Acknowledge Status'
                  options={acknowledgeOptions}
                  defaultValue={
                    siteAcknowledgeFilter === undefined ? '' : siteAcknowledgeFilter.toString()
                  }
                  onChange={handleAcknowledgeFilterChange}
                  placeholder='All'
                />
              </div>
              <div className='col-md-3 d-flex align-items-end'>
                <div className='d-flex gap-2'>
                  <button
                    className='btn btn-primary'
                    onClick={applyDateFilters}
                    disabled={isLoading}
                  >
                    <i className='fas fa-filter me-2'></i>
                    {intl.formatMessage({ id: 'ECOMMERCE.COMMON.FILTER' })}
                  </button>
                  <button
                    className='btn btn-light'
                    onClick={resetDateFilters}
                    disabled={
                      isLoading ||
                      (!dateRange && siteAcknowledgeFilter === undefined && companyFilter === null)
                    }
                  >
                    <i className='fas fa-times me-2'></i>
                    {intl.formatMessage({ id: 'COMMON.RESET' })}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderPagination = () => {
    if (props.data.pagination.last_page <= 1) return null

    return (
      <div className='d-flex justify-content-center mt-4'>
        <nav aria-label='Page navigation'>
          <ul className='pagination'>
            <li
              className={`page-item ${props.data.pagination.current_page === 1 ? 'disabled' : ''}`}
            >
              <button
                className='page-link'
                onClick={() => handlePageChange(props.data.pagination.current_page - 1)}
                disabled={props.data.pagination.current_page === 1 || isLoading}
              >
                {intl.formatMessage({ id: 'COMMON.PREVIOUS' })}
              </button>
            </li>

            {Array.from({ length: props.data.pagination.last_page }, (_, i) => i + 1).map(
              (page) => (
                <li
                  key={page}
                  className={`page-item ${
                    props.data.pagination.current_page === page ? 'active' : ''
                  }`}
                >
                  <button
                    className='page-link'
                    onClick={() => handlePageChange(page)}
                    disabled={isLoading}
                  >
                    {page}
                  </button>
                </li>
              )
            )}

            <li
              className={`page-item ${
                props.data.pagination.current_page === props.data.pagination.last_page
                  ? 'disabled'
                  : ''
              }`}
            >
              <button
                className='page-link'
                onClick={() => handlePageChange(props.data.pagination.current_page + 1)}
                disabled={
                  props.data.pagination.current_page === props.data.pagination.last_page ||
                  isLoading
                }
              >
                {intl.formatMessage({ id: 'COMMON.NEXT' })}
              </button>
            </li>
          </ul>
        </nav>
      </div>
    )
  }

  const renderTreeView = () => {
    if (!props.data?.data) return null

    // Sort dates in descending order (newest first)
    const sortedDates = Object.keys(props.data.data).sort((a, b) => {
      return new Date(b).getTime() - new Date(a).getTime()
    })

    // Check if there are any alarms to display
    const hasAlarms = sortedDates.length > 0

    return (
      <div className='card mb-5 mb-xl-8'>
        <div className='card-body'>
          {isLoading ? (
            <div className='d-flex justify-content-center p-5'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>
                  {intl.formatMessage({ id: 'COMMON.LOADING' })}
                </span>
              </div>
            </div>
          ) : !hasAlarms ? (
            <div className='d-flex flex-column align-items-center justify-content-center p-8'>
              <div className='text-center'>
                <div className='mb-4'>
                  <i className='fas fa-check-circle fs-1 text-success'></i>
                </div>
                <h4 className='text-success mb-2'>All Clear!</h4>
                <p className='text-muted fs-6'>No alarms detected for the selected criteria.</p>
              </div>
            </div>
          ) : (
            <>
              <div className='tree-view'>
                {sortedDates.map((date, dateIndex) => {
                  const formattedDate = formatDate(date)
                  const dateData = props.data.data[date]
                  const isDateExpanded = expandedDates[date]

                  // Calculate total alarms for this date
                  const totalAlarms = Object.values(dateData).reduce(
                    (sum, company) => sum + company.company_alarm_count,
                    0
                  )

                  return (
                    <div className='tree-item' key={`date-${dateIndex}`}>
                      <div
                        className={`tree-node date-node ${isDateExpanded ? 'expanded' : ''}`}
                        onClick={() => toggleDateExpansion(date)}
                      >
                        <div className='d-flex align-items-center'>
                          <span className='tree-toggle me-2'>
                            {isDateExpanded ? (
                              <i className='fas fa-chevron-down'></i>
                            ) : (
                              <i className='fas fa-chevron-right'></i>
                            )}
                          </span>
                          <span className='tree-label'>{formattedDate}</span>
                          <span className='badge badge-primary ms-2'>
                            {intl.formatMessage({ id: 'COMMON.TOTAL' })}: {totalAlarms}
                          </span>
                        </div>
                      </div>

                      {isDateExpanded && (
                        <div className='tree-children'>
                          {Object.entries(dateData).map(([companyId, company], companyIndex) => {
                            const companyKey = `${date}-${companyId}`
                            const isCompanyExpanded = expandedCompanies[companyKey]

                            return (
                              <div
                                className='tree-item'
                                key={`company-${dateIndex}-${companyIndex}`}
                              >
                                <div
                                  className={`tree-node company-node mt-3 ${
                                    isCompanyExpanded ? 'expanded' : ''
                                  }`}
                                  onClick={() => toggleCompanyExpansion(date, companyId)}
                                >
                                  <div className='d-flex align-items-center'>
                                    <span className='tree-toggle me-2'>
                                      {isCompanyExpanded ? (
                                        <i className='fas fa-chevron-down'></i>
                                      ) : (
                                        <i className='fas fa-chevron-right'></i>
                                      )}
                                    </span>
                                    <span className='tree-label'>{company.company_name}</span>
                                    <div className='ms-2 d-flex gap-2'>
                                      <span className='badge badge-primary'>
                                        {intl.formatMessage({ id: 'COMMON.TOTAL' })}:{' '}
                                        {company.company_alarm_count}
                                      </span>
                                      {siteAcknowledgeFilter === undefined && (
                                        <>
                                          <span className='badge badge-success'>
                                            {intl.formatMessage({ id: 'ALARMS.ACKNOWLEDGED' })}:{' '}
                                            {company.sites
                                              .filter((site) => site.site_acknowledge)
                                              .reduce((sum, site) => sum + site.alarm_count, 0)}
                                          </span>
                                          <span className='badge badge-danger'>
                                            {intl.formatMessage({ id: 'ALARMS.UNACKNOWLEDGED' })}:{' '}
                                            {company.sites
                                              .filter((site) => !site.site_acknowledge)
                                              .reduce((sum, site) => sum + site.alarm_count, 0)}
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {isCompanyExpanded && (
                                  <div className='tree-children'>
                                    <div className='site-search-container mb-3 mt-3'>
                                      <div className='input-group'>
                                        <span className='input-group-text'>
                                          <i className='fas fa-search'></i>
                                        </span>
                                        <input
                                          type='text'
                                          className='form-control'
                                          placeholder={intl.formatMessage({
                                            id: 'COMMON.SEARCH_SITE',
                                          })}
                                          value={siteFilter}
                                          onChange={(e) => setSiteFilter(e.target.value)}
                                        />
                                        {siteFilter && (
                                          <button
                                            className='btn btn-outline-secondary'
                                            type='button'
                                            onClick={() => setSiteFilter('')}
                                          >
                                            <i className='fas fa-times'></i>
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    <div className='sites-container'>
                                      {company.sites
                                        .filter(
                                          (site) =>
                                            siteFilter === '' ||
                                            site.site_name
                                              .toLowerCase()
                                              .includes(siteFilter.toLowerCase())
                                        )
                                        .map((site, siteIndex) => (
                                          <div
                                            key={`site-${dateIndex}-${companyIndex}-${siteIndex}`}
                                            className={`site-card ${getCardClassName(site)}`}
                                            onClick={() =>
                                              props.editCallback(site.alarm_id, () => {
                                                // Refresh the current page with current filters
                                                const filters: {
                                                  startDate?: Date
                                                  endDate?: Date
                                                  siteAcknowledge?: boolean
                                                  companyId?: number
                                                  alertType?: 'hourly' | 'daily'
                                                } = {}

                                                if (dateRange) {
                                                  const [startDateStr, endDateStr] =
                                                    dateRange.split(' to ')
                                                  if (startDateStr && endDateStr) {
                                                    filters.startDate = new Date(startDateStr)
                                                    filters.endDate = new Date(endDateStr)
                                                  }
                                                }

                                                if (siteAcknowledgeFilter !== undefined) {
                                                  filters.siteAcknowledge = siteAcknowledgeFilter
                                                }

                                                if (companyFilter !== null) {
                                                  filters.companyId = companyFilter
                                                }

                                                filters.alertType = alertTypeFilter

                                                props.fetchData(
                                                  props.data.pagination.current_page,
                                                  filters
                                                )
                                              })
                                            }
                                          >
                                            <div className='site-card-header'>
                                              <div className='site-name'>
                                                <i className='fas fa-building me-2'></i>
                                                {site.site_name}
                                              </div>
                                            </div>
                                            {site.site_acknowledge && site.site_alarm_comment && (
                                              <div className='site-card-comment'>
                                                <i className='fas fa-comment-alt me-2'></i>
                                                {site.site_alarm_comment}
                                              </div>
                                            )}
                                            <div className='site-card-icon'>
                                              <i className='fas fa-chevron-right'></i>
                                            </div>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {renderPagination()}
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      <style>
        {`
        .tree-view {
          padding: 0;
          font-family: 'Inter', sans-serif;
        }
        
        .tree-item {
          margin-bottom: 12px;
          transition: all 0.2s ease;
        }
        
        .tree-node {
          padding: 12px 16px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        }
        
        .tree-node:hover {
          background-color: rgba(0, 0, 0, 0.03);
          transform: translateY(-1px);
          box-shadow: 0 3px 6px rgba(0,0,0,0.08);
        }
        
        .date-node {
          background-color: #f8f9fa;
          border-left: 4px solid #3699ff;
          font-weight: 600;
        }
        
        .company-node {
          background-color: #ffffff;
          border-left: 4px solid #1bc5bd;
          margin-left: 20px;
        }
        
        .tree-children {
          margin-left: 20px;
          padding-left: 10px;
          border-left: 1px dashed #e4e6ef;
        }
        
        .tree-toggle {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          border-radius: 4px;
          background-color: rgba(0,0,0,0.04);
          margin-right: 8px;
          transition: all 0.2s ease;
        }
        
        .tree-node:hover .tree-toggle {
          background-color: rgba(0,0,0,0.08);
        }
        
        .badge {
          font-weight: 500;
          padding: 0.35em 0.65em;
          border-radius: 4px;
        }
        
        .badge-primary {
          background-color: rgba(54, 153, 255, 0.1);
          color: #3699ff;
        }
        
        .badge-success {
          background-color: rgba(80, 205, 137, 0.1);
          color: #50cd89;
        }
        
        .badge-danger {
          background-color: rgba(241, 65, 108, 0.1);
          color: #f1416c;
        }
        
        .sites-container {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 16px;
          padding: 16px 0;
        }
        
        .site-card {
          background-color: #ffffff;
          border-radius: 8px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.05);
          overflow: hidden;
          transition: all 0.2s ease;
          position: relative;
          cursor: pointer;
        }
        
        .site-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(0,0,0,0.1);
        }
        
        .site-card.card-success {
          border-left: 6px solid #50cd89;
          background-color: #e8fff3;
        }
        
        .site-card.card-danger {
          border-left: 6px solid #f1416c;
          background-color: #fff5f8;
        }
        
        .site-card-header {
          padding: 16px;
          border-bottom: 1px solid #f1f1f1;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .site-name {
          font-weight: 500;
          color: #181c32;
        }
        
        .site-card-footer {
          padding: 12px 16px;
          display: flex;
          justify-content: flex-end;
        }
        
        .btn-primary {
          background-color: #3699ff;
          border-color: #3699ff;
        }
        
        .btn-primary:hover {
          background-color: #187de4;
          border-color: #187de4;
        }
        
        .site-search-container {
          margin-bottom: 20px;
        }
        
        .site-search-container .input-group {
          box-shadow: 0 2px 6px rgba(0,0,0,0.05);
          border-radius: 8px;
          overflow: hidden;
        }
        
        .site-search-container .input-group-text {
          background-color: #f8f9fa;
          border: none;
          color: #b5b5c3;
        }
        
        .site-search-container .form-control {
          border: none;
          padding: 12px 16px;
          font-size: 0.95rem;
        }
        
        .site-search-container .form-control:focus {
          box-shadow: none;
        }
        
        .site-search-container .btn-outline-secondary {
          border: none;
          color: #b5b5c3;
        }
        
        .site-search-container .btn-outline-secondary:hover {
          background-color: #f8f9fa;
          color: #181c32;
        }
        
        .date-filters .card {
          border-radius: 8px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.05);
        }
        
        .date-filters .card-title {
          font-weight: 500;
          color: #181c32;
        }
        
        .date-filters .form-label {
          font-weight: 500;
          color: #3f4254;
        }
        
        .date-filters .form-control {
          border-radius: 6px;
          border: 1px solid #e4e6ef;
          padding: 10px 14px;
        }
        
        .date-filters .form-control:focus {
          border-color: #3699ff;
          box-shadow: 0 0 0 0.2rem rgba(54, 153, 255, 0.25);
        }
        
        .site-card-comment {
          padding: 12px 16px;
          border-bottom: 1px solid #f1f1f1;
          color: #7e8299;
          font-size: 0.95rem;
          background-color: rgba(255, 255, 255, 0.7);
        }
        
        .site-card-icon {
          position: absolute;
          top: 12px;
          right: 12px;
          color: #7e8299;
          opacity: 0.5;
          transition: all 0.2s ease;
        }
        
        .site-card:hover .site-card-icon {
          opacity: 1;
          transform: translateX(2px);
        }
        
        @media (max-width: 768px) {
          .sites-container {
            grid-template-columns: 1fr;
          }
          
          .date-filters .col-md-4 {
            margin-bottom: 1rem;
          }
        }
        `}
      </style>

      {renderDateFilters()}
      {renderTreeView()}
    </>
  )
}

export default GroupedAlarmsTable
